// Floating.tsx
// ---------------------------------------------------------------------------
// One anchored layer, rendered into document.body.
//
// Extracted from GuildWeeklyRelease, where it was written for the Planner's
// raid header. The raid sheet needs the same thing for a different reason: its
// lane panels clip their children (rounded card with overflow hidden), so an
// absolutely positioned picker was cut off at the panel edge no matter how
// high its z-index went. Same fix, so the same component.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const VIEWPORT_MARGIN = 12;

/**
 * Anchored layer rendered into document.body.
 *
 * The control sits inside the Planner's raid header, which clips its children
 * (rounded hero art), so an absolutely positioned popover was cut off at the
 * card's edge no matter how high its z-index went. A portal escapes the
 * clipping ancestor entirely; position is then measured from the anchor and
 * clamped to the viewport, flipping sides when there is no room.
 */
export function Floating({
  anchor,
  open,
  align = "left",
  prefer = "bottom",
  className = "",
  children,
}: {
  anchor: HTMLElement | null;
  open: boolean;
  align?: "left" | "center";
  prefer?: "top" | "bottom";
  className?: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const place = useCallback(() => {
    const el = ref.current;
    if (!el || !anchor) return;
    const a = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 8;

    let left = align === "center" ? a.left + a.width / 2 - w / 2 : a.left;
    left = Math.min(Math.max(VIEWPORT_MARGIN, left), Math.max(VIEWPORT_MARGIN, vw - w - VIEWPORT_MARGIN));

    let top = prefer === "top" ? a.top - gap - h : a.bottom + gap;
    if (prefer === "top" && top < VIEWPORT_MARGIN) top = a.bottom + gap;
    if (prefer === "bottom" && top + h > vh - VIEWPORT_MARGIN) {
      top = a.top - gap - h >= VIEWPORT_MARGIN ? a.top - gap - h : Math.max(VIEWPORT_MARGIN, vh - h - VIEWPORT_MARGIN);
    }

    setPos((prev) =>
      prev && Math.abs(prev.top - top) < 0.5 && Math.abs(prev.left - left) < 0.5 ? prev : { top, left }
    );
  }, [anchor, align, prefer]);

  // Measure once the content is in the DOM, so the real height decides whether
  // it flips above.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    place();
  }, [open, place, children]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={ref}
      className={className}
      style={{
        position: "fixed",
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        // Hidden for the one frame before it has been measured, so it never
        // flashes in the top-left corner.
        visibility: pos ? "visible" : "hidden",
        zIndex: 9993,
      }}
    >
      {children}
    </div>,
    document.body
  );
}

/** Every place this control appears, so the explainer can say which one is "here". */

export default Floating;

/**
 * Hover tooltip. Same anchored layer, wrapped so a caller only has to say what
 * the tip says and what it hangs off.
 *
 * Native `title` is free but ugly and slow: a browser-chrome box after a
 * second of hover, unstyled, and clipped to nothing useful on touch. Sheets
 * carry a lot of small explanations ("set on the night sheet", "already has
 * another duty on this boss"), so they are worth rendering properly.
 */
export function Tip({
  tip,
  children,
  className = "",
  as = "span",
  ...rest
}: {
  tip: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  as?: "span" | "div";
  [key: string]: any;
}) {
  const [open, setOpen] = useState(false);
  const [el, setEl] = useState<HTMLElement | null>(null);

  return (
    <>
      {React.createElement(
        as,
        {
          ref: setEl,
          className,
          onMouseEnter: () => setOpen(true),
          onMouseLeave: () => setOpen(false),
          onFocus: () => setOpen(true),
          onBlur: () => setOpen(false),
          ...rest,
        },
        children
      )}
      {tip != null && tip !== "" && (
        <Floating anchor={el} open={open} align="center" prefer="top">
          <div className="pointer-events-none max-w-[280px] rounded-lg border border-white/15
                          bg-[#0A0C10] px-2.5 py-1.5 text-[11px] leading-snug text-white/85
                          shadow-2xl shadow-black/70 ring-1 ring-black/40">
            {tip}
          </div>
        </Floating>
      )}
    </>
  );
}
